/**
 * SpellEffects.ts — school-driven Babylon ParticleSystem factory for spell
 * casts and impacts. Complements AbilityVFX.ts (which keys off `abilityId`)
 * by exposing a coarser dimension matching the server's DamageSchool union
 * (`'fire' | 'frost' | 'holy' | 'shadow' | 'nature' | 'arcane'`) so any
 * combat path can spawn a sensible visual without owning an ability table.
 *
 * Why a separate file?
 *   • AbilityVFX.ts is element-flavoured but only tunes 7 aesthetics
 *     (fire/frost/holy/shadow/arrow/slash/nature). Spells cast/impact need
 *     denser, longer-lived FX with school-specific motion (e.g. arcane beam
 *     vs holy ring expand vs nature radial leaf burst).
 *   • Having `createSpellEffect(scene, school, position, target?)` lets a
 *     server event handler drop an effect with no class/ability lookup.
 *   • Both files share the same particle texture (inline radial blob).
 *
 * Usage:
 *   import { createSpellEffect } from '../scene/SpellEffects.js';
 *   createSpellEffect(scene, 'fire', casterPos);                  // cast
 *   createSpellEffect(scene, 'fire', impactPos, undefined, 'impact'); // hit
 *
 * Disposal: the returned IDisposable is auto-disposed after lifetime+0.5s.
 * Caller may dispose() earlier if the spell fizzles before the visual ends.
 */

import {
  Color4,
  type IDisposable,
  ParticleSystem,
  type Scene,
  Texture,
  Vector3,
} from '@babylonjs/core';

export type SpellSchool = 'fire' | 'frost' | 'holy' | 'shadow' | 'nature' | 'arcane';
export type SpellPhase = 'cast' | 'impact' | 'channel';

// ── shared 16x16 radial-blob texture (matches AbilityVFX.ts) ───────────
let cachedTex: Texture | null = null;
function getTex(scene: Scene): Texture {
  if (cachedTex) return cachedTex;
  cachedTex = new Texture(
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAdklEQVQ4y2NkAAGGyclEAQYG0v///+9CkD8QwxCBcEYUgwYBeYwR5oRRJjATBAxoQGcr1FFfgY9hABgYRwIzYwIc0ABwYIaiBHsVJEBKf0xC0mgKIEmKZCFAvk0CYBUjBXwBA8MwgB6BSDAqMCowKjAqMCowQEgADQYHzkLLHbcAAAAASUVORK5CYII=',
    scene,
    true,
    false,
  );
  return cachedTex;
}

// ── per-school palette (color1 → color2 → colorDead) ──────────────────
interface SchoolPalette {
  c1: Color4;
  c2: Color4;
  dead: Color4;
}

const PALETTE: Record<SpellSchool, SchoolPalette> = {
  fire: {
    c1:   new Color4(1.00, 0.65, 0.10, 1),
    c2:   new Color4(1.00, 0.20, 0.05, 1),
    dead: new Color4(0.30, 0.05, 0.00, 0),
  },
  frost: {
    c1:   new Color4(0.65, 0.90, 1.00, 1),
    c2:   new Color4(0.40, 0.70, 1.00, 0.8),
    dead: new Color4(0.85, 0.95, 1.00, 0),
  },
  holy: {
    c1:   new Color4(1.00, 0.95, 0.60, 1),
    c2:   new Color4(1.00, 1.00, 0.85, 0.9),
    dead: new Color4(1.00, 0.95, 0.55, 0),
  },
  shadow: {
    c1:   new Color4(0.60, 0.20, 0.75, 1),
    c2:   new Color4(0.20, 0.05, 0.35, 0.9),
    dead: new Color4(0.05, 0.00, 0.10, 0),
  },
  nature: {
    c1:   new Color4(0.55, 0.95, 0.45, 1),
    c2:   new Color4(0.20, 0.70, 0.20, 0.85),
    dead: new Color4(0.10, 0.40, 0.10, 0),
  },
  arcane: {
    c1:   new Color4(0.85, 0.55, 1.00, 1),
    c2:   new Color4(0.55, 0.30, 0.95, 0.9),
    dead: new Color4(0.30, 0.10, 0.55, 0),
  },
};

interface SchoolTuning {
  /** Particle capacity. */
  capacity: number;
  /** Emit rate, particles/s. */
  emitRate: number;
  /** Per-particle lifetime range (s). */
  life: [number, number];
  /** Particle size range (m). */
  size: [number, number];
  /** Gravity (m/s², Y component). Positive = float up. */
  gravity: number;
  /** Initial direction1 / direction2. */
  dir1: Vector3;
  dir2: Vector3;
  /** Emit power range (impulse). */
  power: [number, number];
  /** How long the system keeps emitting before stop() (ms). */
  emitDurationMs: number;
  /** Vertical offset from passed `position` to anchor the burst (m). */
  yOffset: number;
  /** Blend mode. ADD = bright, STANDARD = blended. */
  blend: number;
}

/** Per-school cast tuning. Hand-tuned to feel WoW-y on a 60Hz client. */
const CAST_CFG: Record<SpellSchool, SchoolTuning> = {
  fire: {
    capacity: 120, emitRate: 200,
    life: [0.6, 1.4], size: [0.35, 1.10],
    gravity: 4.0,
    dir1: new Vector3(-1.2, 1.5, -1.2), dir2: new Vector3(1.2, 3.5, 1.2),
    power: [1.4, 3.0],
    emitDurationMs: 700, yOffset: 1.10,
    blend: ParticleSystem.BLENDMODE_ADD,
  },
  frost: {
    capacity: 140, emitRate: 130,
    life: [0.9, 1.7], size: [0.20, 0.55],
    gravity: -0.8,
    dir1: new Vector3(-1.5, 0.4, -1.5), dir2: new Vector3(1.5, 1.6, 1.5),
    power: [0.8, 1.8],
    emitDurationMs: 750, yOffset: 1.30,
    blend: ParticleSystem.BLENDMODE_ADD,
  },
  holy: {
    capacity: 100, emitRate: 160,
    life: [0.7, 1.3], size: [0.30, 0.85],
    gravity: 1.6,
    // ring-expand: outward+slightly up
    dir1: new Vector3(-2.0, 0.3, -2.0), dir2: new Vector3(2.0, 1.6, 2.0),
    power: [1.6, 3.4],
    emitDurationMs: 600, yOffset: 1.10,
    blend: ParticleSystem.BLENDMODE_ADD,
  },
  shadow: {
    capacity: 110, emitRate: 150,
    life: [0.8, 1.5], size: [0.30, 0.80],
    gravity: 1.0,
    dir1: new Vector3(-1.0, 0.6, -1.0), dir2: new Vector3(1.0, 2.4, 1.0),
    power: [1.0, 2.4],
    emitDurationMs: 750, yOffset: 1.10,
    blend: ParticleSystem.BLENDMODE_ADD,
  },
  nature: {
    capacity: 90, emitRate: 130,
    life: [0.7, 1.4], size: [0.25, 0.70],
    gravity: 1.2,
    // radial leaf-puff: wider horizontal spread, less vertical
    dir1: new Vector3(-2.4, 0.3, -2.4), dir2: new Vector3(2.4, 1.4, 2.4),
    power: [0.8, 2.0],
    emitDurationMs: 700, yOffset: 0.50,
    blend: ParticleSystem.BLENDMODE_ADD,
  },
  arcane: {
    capacity: 130, emitRate: 220,
    life: [0.5, 1.0], size: [0.18, 0.55],
    gravity: 0.0,
    // beam-and-sparkle — narrow vertical column with sparkle spread
    dir1: new Vector3(-0.8, 1.4, -0.8), dir2: new Vector3(0.8, 4.0, 0.8),
    power: [2.0, 4.0],
    emitDurationMs: 650, yOffset: 1.10,
    blend: ParticleSystem.BLENDMODE_ADD,
  },
};

/** Impact tuning — denser burst, shorter lifetime. */
const IMPACT_CFG: Record<SpellSchool, SchoolTuning> = {
  fire: {
    capacity: 80, emitRate: 600,
    life: [0.30, 0.65], size: [0.25, 0.85],
    gravity: 2.0,
    dir1: new Vector3(-2.4, 0.6, -2.4), dir2: new Vector3(2.4, 3.0, 2.4),
    power: [1.8, 3.8],
    emitDurationMs: 120, yOffset: 0.80,
    blend: ParticleSystem.BLENDMODE_ADD,
  },
  frost: {
    capacity: 90, emitRate: 500,
    life: [0.45, 0.95], size: [0.18, 0.50],
    gravity: -3.0,
    dir1: new Vector3(-2.4, 0.5, -2.4), dir2: new Vector3(2.4, 1.6, 2.4),
    power: [1.4, 3.0],
    emitDurationMs: 130, yOffset: 0.80,
    blend: ParticleSystem.BLENDMODE_ADD,
  },
  holy: {
    capacity: 70, emitRate: 700,
    life: [0.30, 0.60], size: [0.30, 0.95],
    gravity: 0.5,
    dir1: new Vector3(-3.0, 0.2, -3.0), dir2: new Vector3(3.0, 0.8, 3.0),
    power: [2.4, 4.4],
    emitDurationMs: 100, yOffset: 0.80,
    blend: ParticleSystem.BLENDMODE_ADD,
  },
  shadow: {
    capacity: 80, emitRate: 500,
    life: [0.35, 0.75], size: [0.25, 0.75],
    gravity: -1.0,
    dir1: new Vector3(-2.0, 0.4, -2.0), dir2: new Vector3(2.0, 2.0, 2.0),
    power: [1.4, 3.0],
    emitDurationMs: 130, yOffset: 0.80,
    blend: ParticleSystem.BLENDMODE_ADD,
  },
  nature: {
    capacity: 70, emitRate: 600,
    life: [0.30, 0.65], size: [0.20, 0.65],
    gravity: -2.0,
    dir1: new Vector3(-3.0, 0.4, -3.0), dir2: new Vector3(3.0, 2.0, 3.0),
    power: [1.6, 3.0],
    emitDurationMs: 110, yOffset: 0.40,
    blend: ParticleSystem.BLENDMODE_ADD,
  },
  arcane: {
    capacity: 100, emitRate: 800,
    life: [0.20, 0.55], size: [0.14, 0.45],
    gravity: 0.0,
    dir1: new Vector3(-2.8, 0.6, -2.8), dir2: new Vector3(2.8, 2.4, 2.8),
    power: [2.0, 4.6],
    emitDurationMs: 100, yOffset: 0.80,
    blend: ParticleSystem.BLENDMODE_ADD,
  },
};

/** Channel tuning (e.g. mind-flay, drain-life) — sustained emit. */
const CHANNEL_DURATION_MS = 2000;

/**
 * Spawn a school-tuned particle burst for a spell cast or impact.
 *
 * @param scene   the active Babylon Scene
 * @param school  one of the 6 magical schools (physical handled by AbilityVFX)
 * @param position the world-space anchor (caster origin for `cast`,
 *                 victim origin for `impact`, caster for `channel`)
 * @param target   reserved for future projectile-target visual; unused today
 * @param phase    `'cast'` (default) | `'impact'` | `'channel'`
 *
 * Returns an IDisposable so the caller can manually cut the spell short
 * (interrupt, unit death, line-of-sight). The caller is NOT required to
 * dispose — the system auto-cleans after lifetime + 0.5s grace.
 */
export function createSpellEffect(
  scene: Scene,
  school: SpellSchool,
  position: Vector3,
  target?: Vector3,
  phase: SpellPhase = 'cast',
): IDisposable {
  void target; // currently unused; kept in signature for future projectile use
  const cfg = phase === 'impact' ? IMPACT_CFG[school] : CAST_CFG[school];
  const palette = PALETTE[school];

  const ps = new ParticleSystem(
    `spfx-${school}-${phase}-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
    cfg.capacity,
    scene,
  );
  ps.particleTexture = getTex(scene);

  // Anchor: clone the position so subsequent caller mutation (e.g. caster
  // moves mid-cast) doesn't drag the burst with them.
  const anchor = position.clone();
  anchor.y += cfg.yOffset;
  ps.emitter = anchor;
  ps.minEmitBox = new Vector3(-0.20, 0, -0.20);
  ps.maxEmitBox = new Vector3(0.20, 0.30, 0.20);

  ps.color1 = palette.c1;
  ps.color2 = palette.c2;
  ps.colorDead = palette.dead;
  ps.minSize = cfg.size[0];
  ps.maxSize = cfg.size[1];
  ps.minLifeTime = cfg.life[0];
  ps.maxLifeTime = cfg.life[1];
  ps.emitRate = cfg.emitRate;
  ps.blendMode = cfg.blend;
  ps.gravity = new Vector3(0, cfg.gravity, 0);
  ps.direction1 = cfg.dir1;
  ps.direction2 = cfg.dir2;
  ps.minAngularSpeed = 0;
  ps.maxAngularSpeed = Math.PI;
  ps.minEmitPower = cfg.power[0];
  ps.maxEmitPower = cfg.power[1];
  ps.updateSpeed = 0.012;

  ps.start();

  const emitDuration = phase === 'channel' ? CHANNEL_DURATION_MS : cfg.emitDurationMs;
  const stopTimer = setTimeout(() => ps.stop(), emitDuration);
  const disposeTimer = setTimeout(
    () => {
      // dispose() is safe to call multiple times — Babylon no-ops on repeat.
      try { ps.dispose(); } catch { /* already disposed */ }
    },
    // life cap so trailing particles fully fade
    emitDuration + Math.ceil(cfg.life[1] * 1000) + 500,
  );

  let disposed = false;
  return {
    dispose() {
      if (disposed) return;
      disposed = true;
      clearTimeout(stopTimer);
      clearTimeout(disposeTimer);
      try { ps.stop(); } catch { /* already stopped */ }
      try { ps.dispose(); } catch { /* already disposed */ }
    },
  };
}

/**
 * Convenience: classId → most-common spell school. Used by combat handlers
 * that don't have an explicit school field on the ability event.
 */
export function defaultSchoolForClass(classId: string): SpellSchool {
  switch (classId) {
    case 'mage':       return 'frost';
    case 'warlock':    return 'shadow';
    case 'cleric':
    case 'priest':
    case 'paladin':    return 'holy';
    case 'druid':
    case 'shaman':     return 'nature';
    default:           return 'arcane';
  }
}

/**
 * Heuristic: ability id → school. Used when the server event only carries
 * an abilityId. Falls back through keyword matching.
 */
export function inferSpellSchool(abilityId: string): SpellSchool {
  const id = abilityId.toLowerCase();
  if (/fire|flame|burn|pyro|inferno|meteor|combust|incinerat|immol/.test(id)) return 'fire';
  if (/frost|ice|chill|cold|freeze|cone-of|blizzard/.test(id)) return 'frost';
  if (/holy|smite|judgement|holy-fire|holy-light|consecrat|exorcism|flash-heal|greater-heal|heal|renew/.test(id)) return 'holy';
  if (/shadow|drain|curse|fear|corrupt|deathcoil|dark|void|nightmare|seed|haunt/.test(id)) return 'shadow';
  if (/nature|poison|venom|root|moonfire|wrath|starfall|thunder|lightning|chain-lightning|earthbind|entangle|insect/.test(id)) return 'nature';
  if (/arcane|missiles|polymorph|blink|spellsteal|mind-blast|mind-flay/.test(id)) return 'arcane';
  // physical abilities have no school here — caller should skip SpellEffects.
  return 'arcane';
}
